import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Package, MapPin, Heart, LogOut, ChevronRight, Edit2, Trash2, Star, X } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useAuth } from '../hooks/useAuth';
import { ordersAPI } from '../api/orders';
import { authAPI } from '../api/auth';
import { Order, Address } from '../types';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

type Tab = 'profile' | 'orders' | 'addresses' | 'wishlist';

const AccountPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const [orders, setOrders] = useState<Order[]>([]);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [wishlist, setWishlist] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    country: '',
  });
  const { user, updateUser } = useAuthStore();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [addressFormData, setAddressFormData] = useState({
    first_name: '',
    last_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    is_default: false,
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone || '',
        country: (user as any).country || '',
      });

      // Auto-detect country from IP the first time, same as currency
      // detection — only if the account doesn't already have one saved.
      if (!(user as any).country) {
        fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(3000) })
          .then((res) => (res.ok ? res.json() : null))
          .then((data) => {
            if (data?.country_name) {
              setProfileData((prev) => ({ ...prev, country: data.country_name }));
            }
          })
          .catch(() => {});
      }
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'orders') fetchOrders();
    if (activeTab === 'addresses') fetchAddresses();
    if (activeTab === 'wishlist') fetchWishlist();
  }, [activeTab]);

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const res = await ordersAPI.getMyOrders();
      if (res.success && res.data) setOrders(res.data as Order[]);
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/api/addresses');
      if (res.data.success) setAddresses(res.data.data);
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const resetAddressForm = () => {
    setAddressFormData({
      first_name: '', last_name: '', address_line1: '', address_line2: '',
      city: '', state: '', postal_code: '', country: '', is_default: false,
    });
    setEditingAddressId(null);
  };

  const handleAddNewAddress = () => {
    resetAddressForm();
    setShowAddressForm(true);
  };

  const handleEditAddress = (address: Address) => {
    setEditingAddressId(address.id);
    setAddressFormData({
      first_name: address.first_name || '',
      last_name: address.last_name || '',
      address_line1: address.address_line1 || '',
      address_line2: address.address_line2 || '',
      city: address.city || '',
      state: address.state || '',
      postal_code: address.postal_code || '',
      country: address.country || '',
      is_default: address.is_default || false,
    });
    setShowAddressForm(true);
  };

  const handleSaveAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSavingAddress(true);
      if (editingAddressId) {
        await api.put(`/api/addresses/${editingAddressId}`, addressFormData);
        toast.success('Address updated');
      } else {
        await api.post('/api/addresses', addressFormData);
        toast.success('Address added');
      }
      setShowAddressForm(false);
      resetAddressForm();
      fetchAddresses();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save address');
    } finally {
      setIsSavingAddress(false);
    }
  };

  const handleDeleteAddress = async (id: string) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await api.delete(`/api/addresses/${id}`);
      toast.success('Address deleted');
      fetchAddresses();
    } catch {
      toast.error('Failed to delete address');
    }
  };

  const handleSetDefaultAddress = async (id: string) => {
    try {
      await api.put(`/api/addresses/${id}/default`);
      toast.success('Default address updated');
      fetchAddresses();
    } catch {
      toast.error('Failed to update default address');
    }
  };

  const fetchWishlist = async () => {
    try {
      setIsLoading(true);
      const res = await api.get('/api/users/wishlist');
      if (res.data.success) setWishlist(res.data.data);
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const res = await authAPI.updateProfile(profileData);
      if (res.success && res.data) {
        updateUser(res.data);
        toast.success('Profile updated');
      }
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const tabs = [
    { id: 'profile' as Tab, label: 'Profile', icon: User },
    { id: 'orders' as Tab, label: 'Orders', icon: Package },
    { id: 'addresses' as Tab, label: 'Addresses', icon: MapPin },
    { id: 'wishlist' as Tab, label: 'Wishlist', icon: Heart },
  ];

  const statusColors: Record<string, string> = {
    pending: 'text-amber-600',
    processing: 'text-blue-600',
    shipped: 'text-purple-600',
    delivered: 'text-green-600',
    cancelled: 'text-red-500',
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 animate-fade-in">
      <div className="mb-12">
        <p className="text-xs tracking-widest uppercase text-nude mb-2">Account</p>
        <h1 className="font-serif text-4xl text-primary">
          Welcome, {user?.first_name}
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`w-full flex items-center justify-between px-4 py-3 text-xs tracking-widest uppercase transition-colors ${
                  activeTab === id
                    ? 'bg-primary text-white'
                    : 'text-primary hover:bg-light'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Icon size={14} />
                  <span>{label}</span>
                </div>
                <ChevronRight size={12} />
              </button>
            ))}

            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 text-xs tracking-widest uppercase text-nude hover:text-primary transition-colors"
            >
              <LogOut size={14} />
              <span>Sign Out</span>
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div>
                <h2 className="font-serif text-2xl text-primary mb-8">
                  Personal Information
                </h2>
                <form onSubmit={handleUpdateProfile} className="space-y-6 max-w-md">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={profileData.first_name}
                        onChange={(e) => setProfileData({
                          ...profileData,
                          first_name: e.target.value
                        })}
                        className="input-luxury"
                      />
                    </div>
                    <div>
                      <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={profileData.last_name}
                        onChange={(e) => setProfileData({
                          ...profileData,
                          last_name: e.target.value
                        })}
                        className="input-luxury"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={user?.email}
                      disabled
                      className="input-luxury opacity-50 cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({
                        ...profileData,
                        phone: e.target.value
                      })}
                      className="input-luxury"
                    />
                  </div>

                  <div>
                    <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                      Country
                    </label>
                    <select
                      value={profileData.country}
                      onChange={(e) => setProfileData({
                        ...profileData,
                        country: e.target.value
                      })}
                      className="input-luxury"
                    >
                      <option value="">SELECT COUNTRY</option>
                      <option value="Nigeria">Nigeria</option>
                      <option value="United States">United States</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="Canada">Canada</option>
                      <option value="Ghana">Ghana</option>
                      <option value="Kenya">Kenya</option>
                      <option value="South Africa">South Africa</option>
                      <option value="France">France</option>
                      <option value="Germany">Germany</option>
                      <option value="Australia">Australia</option>
                      <option value="United Arab Emirates">United Arab Emirates</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary disabled:opacity-50"
                  >
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                </form>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div>
                <h2 className="font-serif text-2xl text-primary mb-8">
                  Order History
                </h2>
                {isLoading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="skeleton h-20 w-full rounded" />
                    ))}
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-16">
                    <Package size={32} className="text-nude mx-auto mb-4" />
                    <p className="text-xs tracking-widest uppercase text-nude">
                      No orders yet
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div
                        key={order.id}
                        className="border border-light p-6 hover:border-nude transition-colors"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="text-xs tracking-widest uppercase font-medium">
                              {order.order_number}
                            </p>
                            <p className="text-xs text-nude mt-1">
                              {new Date(order.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={`text-xs tracking-widest uppercase ${
                              statusColors[order.status] || 'text-primary'
                            }`}>
                              {order.status}
                            </p>
                            <p className="text-sm font-medium mt-1">
                              ${Number(order.total).toFixed(2)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-nude tracking-wide">
                            Payment: {order.payment_status}
                          </p>
                          <button
                            onClick={() => navigate(`/orders/${order.id}`)}
                            className="text-xs tracking-widest uppercase text-primary hover:text-nude transition-colors flex items-center space-x-1"
                          >
                            <span>View</span>
                            <ChevronRight size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Addresses Tab */}
            {activeTab === 'addresses' && (
              <div>
                <div className="flex items-center justify-between mb-8">
                  <h2 className="font-serif text-2xl text-primary">
                    Saved Addresses
                  </h2>
                  <button
                    onClick={handleAddNewAddress}
                    className="btn-secondary text-xs"
                  >
                    Add New
                  </button>
                </div>

                {showAddressForm && (
                  <motion.form
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    onSubmit={handleSaveAddress}
                    className="border border-light p-6 mb-6 space-y-4 overflow-hidden"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-serif text-lg text-primary">
                        {editingAddressId ? 'Edit Address' : 'New Address'}
                      </h3>
                      <button
                        type="button"
                        onClick={() => { setShowAddressForm(false); resetAddressForm(); }}
                        className="text-nude hover:text-primary transition-colors"
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        placeholder="FIRST NAME"
                        value={addressFormData.first_name}
                        onChange={(e) => setAddressFormData({ ...addressFormData, first_name: e.target.value })}
                        className="input-luxury"
                        required
                      />
                      <input
                        placeholder="LAST NAME"
                        value={addressFormData.last_name}
                        onChange={(e) => setAddressFormData({ ...addressFormData, last_name: e.target.value })}
                        className="input-luxury"
                        required
                      />
                    </div>
                    <input
                      placeholder="ADDRESS LINE 1"
                      value={addressFormData.address_line1}
                      onChange={(e) => setAddressFormData({ ...addressFormData, address_line1: e.target.value })}
                      className="input-luxury"
                      required
                    />
                    <input
                      placeholder="ADDRESS LINE 2 (OPTIONAL)"
                      value={addressFormData.address_line2}
                      onChange={(e) => setAddressFormData({ ...addressFormData, address_line2: e.target.value })}
                      className="input-luxury"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        placeholder="CITY"
                        value={addressFormData.city}
                        onChange={(e) => setAddressFormData({ ...addressFormData, city: e.target.value })}
                        className="input-luxury"
                        required
                      />
                      <input
                        placeholder="STATE"
                        value={addressFormData.state}
                        onChange={(e) => setAddressFormData({ ...addressFormData, state: e.target.value })}
                        className="input-luxury"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        placeholder="POSTAL CODE"
                        value={addressFormData.postal_code}
                        onChange={(e) => setAddressFormData({ ...addressFormData, postal_code: e.target.value })}
                        className="input-luxury"
                        required
                      />
                      <select
                        value={addressFormData.country}
                        onChange={(e) => setAddressFormData({ ...addressFormData, country: e.target.value })}
                        className="input-luxury"
                        required
                      >
                        <option value="">SELECT COUNTRY</option>
                        <option value="Nigeria">Nigeria</option>
                        <option value="United States">United States</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Canada">Canada</option>
                        <option value="Ghana">Ghana</option>
                        <option value="Kenya">Kenya</option>
                        <option value="South Africa">South Africa</option>
                        <option value="France">France</option>
                        <option value="Germany">Germany</option>
                        <option value="Australia">Australia</option>
                        <option value="United Arab Emirates">United Arab Emirates</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={addressFormData.is_default}
                        onChange={(e) => setAddressFormData({ ...addressFormData, is_default: e.target.checked })}
                        className="accent-primary"
                      />
                      <span className="text-xs tracking-widest uppercase text-secondary">Set as default</span>
                    </label>
                    <button type="submit" disabled={isSavingAddress} className="btn-primary text-xs disabled:opacity-50">
                      {isSavingAddress ? 'Saving...' : editingAddressId ? 'Update Address' : 'Save Address'}
                    </button>
                  </motion.form>
                )}

                {isLoading ? (
                  <div className="grid grid-cols-2 gap-4">
                    {[1, 2].map((i) => (
                      <div key={i} className="skeleton h-32 w-full rounded" />
                    ))}
                  </div>
                ) : addresses.length === 0 ? (
                  <div className="text-center py-16">
                    <MapPin size={32} className="text-nude mx-auto mb-4" />
                    <p className="text-xs tracking-widest uppercase text-nude">
                      No addresses saved
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {addresses.map((address) => (
                      <div
                        key={address.id}
                        className={`border p-6 ${
                          address.is_default
                            ? 'border-primary'
                            : 'border-light'
                        }`}
                      >
                        {address.is_default && (
                          <span className="text-xs tracking-widest uppercase text-nude block mb-3">
                            Default
                          </span>
                        )}
                        <p className="text-sm font-medium">
                          {address.first_name} {address.last_name}
                        </p>
                        <p className="text-xs text-secondary mt-1 leading-relaxed">
                          {address.address_line1}
                          {address.address_line2 && `, ${address.address_line2}`}
                          <br />
                          {address.city}, {address.state} {address.postal_code}
                          <br />
                          {address.country}
                        </p>
                        <div className="flex items-center space-x-4 mt-4 pt-4 border-t border-light">
                          <button
                            onClick={() => handleEditAddress(address)}
                            className="flex items-center space-x-1 text-xs text-nude hover:text-primary transition-colors"
                          >
                            <Edit2 size={12} />
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => handleDeleteAddress(address.id)}
                            className="flex items-center space-x-1 text-xs text-nude hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={12} />
                            <span>Delete</span>
                          </button>
                          {!address.is_default && (
                            <button
                              onClick={() => handleSetDefaultAddress(address.id)}
                              className="flex items-center space-x-1 text-xs text-nude hover:text-primary transition-colors"
                            >
                              <Star size={12} />
                              <span>Set Default</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Wishlist Tab */}
            {activeTab === 'wishlist' && (
              <div>
                <h2 className="font-serif text-2xl text-primary mb-8">
                  Saved Items
                </h2>
                {isLoading ? (
                  <div className="grid grid-cols-2 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="skeleton aspect-[3/4] w-full rounded" />
                    ))}
                  </div>
                ) : wishlist.length === 0 ? (
                  <div className="text-center py-16">
                    <Heart size={32} className="text-nude mx-auto mb-4" />
                    <p className="text-xs tracking-widest uppercase text-nude">
                      No saved items
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    {wishlist.map((item) => (
                      <div key={item.id} className="group">
                        <div className="aspect-[3/4] bg-light overflow-hidden">
                          {item.image_url && (
                            <img
                              src={item.image_url}
                              alt={item.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          )}
                        </div>
                        <div className="pt-3">
                          <p className="text-xs font-medium tracking-wide">{item.name}</p>
                          <p className="text-xs text-nude mt-1">
                            ${Number(item.price).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;